// =============================================================================
// api/hotspots.ts
// =============================================================================
import type {
  AreaHotspotType,
  FloorMapHotspotType,
  Hotspot,
  HotspotImage,
  Media,
  MediaHotspotType,
  NavHotspotType,
  NavLabelKind,
  NavLabelPos,
  NavSignDir,
  NavSignStyle,
  Position3D,
  UpsertHotspotPayload,
  UUID,
  VHotspot,
  VideoHotspotType,
  VideoIframeHotspotType,
} from "@/types";
import { supabase } from "@cl/supabase";
import { handleSupabaseError } from "./_error";
import { deleteMedia, persistHotspotImages, replaceMediaRecord } from "./media";

export const mapHotspotRowToHotspot = (row: VHotspot): Hotspot => {
  const base = {
    id: row.id,
    scene_id: row.scene_id,
    type: row.type,
    // "" es placeholder, no contenido: normalizar a null aca deja truthful a
    // todos los consumidores (varios usan `??`, que no atrapa el string vacio).
    title: row.title?.trim() || null,
    description: row.description ?? null,
    translations: row.translations ?? {},
    content_ref_id: row.content_ref_id ?? null,
    link_url: row.link_url ?? null,
    sort_order: row.sort_order,
    img_id: row.img_id ?? null,
    img_url: row.img_url ?? null,
    scale: row.scale ?? 1.0,
    no_fill: row.no_fill ?? false,
    animate: row.animate ?? true,
    wall_mode: row.wall_mode ?? false,
    // Tri-estado: null = auto (alpha de la imagen), true/false = forzado por el dueno.
    frameless: row.frameless ?? null,
    wall_rotation: row.wall_rotation ?? 0,
    img_shape: row.img_shape ?? "rect",
    img_border: row.img_border ?? false,
    images: row.images ?? [],
  };

  if (row.type === "Nav" || row.type === "NavPreview") {
    let nav_shape: "circle" | "arrow" | "sign" = "circle";
    let nav_rotation = 0;
    let nav_label_pos: NavLabelPos | undefined;
    let nav_label_hover: boolean | undefined;
    let nav_show_label: boolean | undefined;
    let nav_label_kind: NavLabelKind | undefined;
    let nav_sign_style: NavSignStyle | undefined;
    let nav_sign_dir: NavSignDir | undefined;
    if (row.description) {
      if (row.description === "arrow") {
        nav_shape = "arrow"; // legacy format
      } else {
        try {
          const parsed = JSON.parse(row.description) as { shape?: string; rotation?: number; labelPos?: string; labelHover?: boolean; showLabel?: boolean; labelKind?: string; signStyle?: string; signDir?: string };
          if (parsed.shape === "arrow" || parsed.shape === "sign")
            nav_shape = parsed.shape;
          nav_rotation = parsed.rotation ?? 0;
          if (parsed.labelPos === "above" || parsed.labelPos === "floor") {
            nav_label_pos = parsed.labelPos;
          }
          if (typeof parsed.labelHover === "boolean") nav_label_hover = parsed.labelHover;
          if (typeof parsed.showLabel === "boolean") nav_show_label = parsed.showLabel;
          if (parsed.labelKind === "room" || parsed.labelKind === "scene") {
            nav_label_kind = parsed.labelKind;
          }
          if (
            parsed.signStyle === "plate" ||
            parsed.signStyle === "chevron" ||
            parsed.signStyle === "underline" ||
            parsed.signStyle === "arrow"
          ) {
            nav_sign_style = parsed.signStyle;
          }
          if (
            parsed.signDir === "left" ||
            parsed.signDir === "right" ||
            parsed.signDir === "up" ||
            parsed.signDir === "down"
          ) {
            nav_sign_dir = parsed.signDir;
          }
        } catch { /* unknown format, keep defaults */ }
      }
    }
    return {
      ...base,
      type: row.type,
      target_scene_id: row.target_scene_id!,
      position: row.position!,
      points: null,
      nav_shape,
      nav_rotation,
      nav_label_pos,
      nav_label_hover,
      nav_show_label,
      nav_label_kind,
      nav_sign_style,
      nav_sign_dir,
    } as NavHotspotType;
  }

  if (row.type === "Area") {
    return {
      ...base,
      type: row.type,
      target_scene_id: null,
      position: null,
      points: row.points ?? [],
    } as AreaHotspotType;
  }

  if (row.type === "Video") {
    return {
      ...base,
      type: row.type,
      target_scene_id: null,
      position: row.position!,
      points: null,
      width: row.width ?? null,
      height: row.height ?? null,
      video_id: row.video_id,
      video_url: row.video_url ?? null,
      external_url: row.external_url ?? null,
    } as VideoHotspotType;
  }

  if (row.type === "VideoIframe") {
    return {
      ...base,
      type: row.type,
      target_scene_id: null,
      position: row.position!,
      points: null,
      width: row.width ?? null,
      height: row.height ?? null,
      external_url: row.external_url ?? null,
      video_id: row.video_id ?? null,
      video_url: row.video_url ?? null,
    } as VideoIframeHotspotType;
  }

  if (row.type === "FloorMap") {
    return {
      ...base,
      type: "FloorMap",
      target_scene_id: null,
      position: row.position!,
      points: null,
      width: row.width ?? null,
      height: row.height ?? null,
      external_url: row.external_url ?? null,
    } as FloorMapHotspotType;
  }

  // Info | Audio | Img
  return {
    ...base,
    type: row.type,
    target_scene_id: null,
    position: row.position!,
    points: null,
    audio_id: row.audio_id ?? null,
    audio_url: row.audio_url ?? null,
  } as MediaHotspotType;
};

export const fetchHotspots = async (sceneId: string): Promise<Hotspot[]> => {
  const { data, error } = await supabase
    .from("v_hotspots")
    .select("*")
    .eq("scene_id", sceneId)
    .order("sort_order", { ascending: true });
  if (error) handleSupabaseError(error, "fetchHotspots");
  return (data ?? []).map(mapHotspotRowToHotspot);
};

export const fetchHotspotsBySceneIds = async (sceneIds: string[]): Promise<Hotspot[]> => {
  if (!sceneIds.length) return [];
  const { data, error } = await supabase
    .from("v_hotspots")
    .select("*")
    .in("scene_id", sceneIds)
    .order("sort_order", { ascending: true });
  if (error) handleSupabaseError(error, "fetchHotspotsBySceneIds");
  return (data ?? []).map(mapHotspotRowToHotspot);
};

export const upsertHotspot = async (
  payload: UpsertHotspotPayload,
  userId?: string,
  tourId?: string,
): Promise<void> => {
  // 1. Extraemos campos de VISTA. `images` sale SIEMPRE del rest: la columna
  // solo se escribe vía imagesDraft (reconcile) — así un hotspot linkeado
  // (content_ref) nunca copia la galería coalesced de su fuente a su propia fila.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mediaFiles, imagesDraft, id, img_url: _img_url, audio_url: _audio_url, video_url: _video_url, images: _images, ...rest } = payload as UpsertHotspotPayload & {
    img_url?: string | null;
    audio_url?: string | null;
    video_url?: string | null;
    images?: unknown;
  };

  const mediaUpdates: Record<string, string | null> = {};

  const baseRest = rest as {
    img_id?: UUID | null;
    audio_id?: UUID | null;
    video_id?: UUID | null;
    tour_id?: string;
  };

  if (mediaFiles && userId && tourId) {
    const [img_id, audio_id, video_id] = await Promise.all([
      replaceMediaRecord(baseRest.img_id, mediaFiles.img, "hotspots", userId, tourId),
      replaceMediaRecord(baseRest.audio_id, mediaFiles.audio, "hotspots", userId, tourId),
      replaceMediaRecord(baseRest.video_id, mediaFiles.video, "hotspots", userId, tourId),
    ]);
    if (img_id !== undefined) mediaUpdates.img_id = img_id;
    if (audio_id !== undefined) mediaUpdates.audio_id = audio_id;
    if (video_id !== undefined) mediaUpdates.video_id = video_id;
  }

  // 1b. Carousel: reconcile the ADDITIONAL images list against the row's own
  // (non-coalesced) previous list — deletes removed media, uploads pending files.
  let imagesFinal: HotspotImage[] | undefined;
  if (imagesDraft && userId && tourId) {
    let prev: HotspotImage[] = [];
    if (id) {
      const { data: prevRow } = await supabase
        .from("hotspots")
        .select("images")
        .eq("id", id)
        .single();
      prev = (prevRow?.images ?? []) as HotspotImage[];
    }
    imagesFinal = await persistHotspotImages(imagesDraft, prev, userId, tourId);
  }

  // 2. dbPayload — for Nav/NavPreview map nav_shape → description column
  const dbPayload = { ...rest, ...mediaUpdates } as Record<string, unknown> & { tour_id?: string };
  if (imagesFinal) dbPayload.images = imagesFinal;
  if (dbPayload.type === "Nav" || dbPayload.type === "NavPreview") {
    const shape = dbPayload.nav_shape ?? "circle";
    const rotation = dbPayload.nav_rotation ?? 0;
    const labelPos = dbPayload.nav_label_pos as string | undefined;
    const labelHover = dbPayload.nav_label_hover as boolean | undefined;
    const showLabel = dbPayload.nav_show_label as boolean | undefined;
    const labelKind = dbPayload.nav_label_kind as string | undefined;
    const signStyle = dbPayload.nav_sign_style as string | undefined;
    const signDir = dbPayload.nav_sign_dir as string | undefined;
    dbPayload.description = JSON.stringify({
      shape,
      rotation,
      ...(labelPos ? { labelPos } : {}),
      ...(labelHover !== undefined ? { labelHover } : {}),
      ...(showLabel !== undefined ? { showLabel } : {}),
      ...(labelKind ? { labelKind } : {}),
      ...(signStyle ? { signStyle } : {}),
      ...(signDir ? { signDir } : {}),
    });
    delete dbPayload.nav_shape;
    delete dbPayload.nav_rotation;
    delete dbPayload.nav_label_pos;
    delete dbPayload.nav_label_hover;
    delete dbPayload.nav_show_label;
    delete dbPayload.nav_label_kind;
    delete dbPayload.nav_sign_style;
    delete dbPayload.nav_sign_dir;
  }

  if (id) {
    const { error } = await supabase
      .from("hotspots")
      .update(dbPayload)
      .eq("id", id);
    if (error) handleSupabaseError(error, "upsertHotspot/update");
    return;
  }

  if (!dbPayload.tour_id) {
    if (!tourId) throw new Error("tourId is strictly required to create a hotspot (RLS policy)");
    dbPayload.tour_id = tourId;
  }

  const { error } = await supabase.from("hotspots").insert([dbPayload]);
  if (error) handleSupabaseError(error, "upsertHotspot/insert");
};

export const updateHotspotPos = async (
  id: string,
  position: Position3D,
): Promise<void> => {
  const { error } = await supabase
    .from("hotspots")
    .update({ position })
    .eq("id", id);
  if (error) handleSupabaseError(error, "updateHotspotPos");
};

export const deleteHotspot = async (id: string): Promise<void> => {
  const { data: hotspot } = await supabase
    .from("hotspots")
    .select("img_id, audio_id, video_id, images")
    .eq("id", id)
    .single();

  const { error } = await supabase.from("hotspots").delete().eq("id", id);
  if (error) handleSupabaseError(error, "deleteHotspot");

  if (hotspot) {
    // Carousel images live in jsonb — invisible to the FK-based GC trigger, so
    // their media rows are cleaned here alongside the FK-column ones.
    const carouselIds = ((hotspot.images ?? []) as HotspotImage[]).map(
      (i) => i.media_id,
    );
    const ids = [
      hotspot.img_id,
      hotspot.audio_id,
      hotspot.video_id,
      ...carouselIds,
    ].filter(Boolean) as string[];
    if (ids.length) {
      const { data: records } = await supabase
        .from("media")
        .select("*")
        .in("id", ids);
      if (records)
        await Promise.allSettled(records.map((m) => deleteMedia(m as Media)));
    }
  }
};
